/* -------------------------------------------------------------------------- */
/* FORGET MIXIN                                                               */
/* -------------------------------------------------------------------------- */
import { v4 as uuid } from 'uuid';
import { digits } from '@nodes/helpers/number';
import YRecaptcha from '@components/Recaptcha';
import YFormTitle from '@rogues/Auth/blocks/FormTitle';

export default {
    name: 'ForgetMixin',

    components: {
        YRecaptcha,
        YFormTitle,
    },
    
    props: {
        /**
         * Color of the links, buttons and forms
         */
        color: String,

        /**
         * Titles
         */
        titles: Object,

        /**
         * Descriptions
         */
        descriptions: Object,

        /**
         * Token Trans
         */
        tokenTrans: Object,

        /**
         * Buttons Trans
         */
        buttonsTrans: Object,

        /**
         * Login After Forget
         */
        loginAfterForget: Boolean,

        /**
         * After Success Reset Password Action
         */
        afterSuccessResetPasswordAction: Function,
    },

    /**
     * @inheritdoc
     */
    data() {
        return {
            keyHelper     : uuid(),
            showPassword  : false,
            showPassword2 : false,
            usernameFields: null,
            recaptcha     : {
                active: false,
            },
            recaptchaToken: null,

            username      : null,
            password      : null,
            password2     : null,
            token         : {},
            state         : 'forget-request',
            showResend    : true,
            counter       : null,
            tokenCharCount: null,
            channels      : null,
        };
    },

    /**
     * @inheritdoc
     */
    created() {
        this.fetchUsernameConfig();
    },

    computed: {
        /**
         * Return the title based on the form state
         *
         * @returns {string}
         */
        title() {
            // eslint-disable-next-line no-unused-vars
            const state = this.state.replace('-', '_');
            const title = this.titles[state];

            return title;
        },

        /**
         * Return the Description based on the form state
         *
         * @returns {string}
         */
        description() {
            // eslint-disable-next-line no-unused-vars
            const state = this.state.replace('-', '_');
            let description = null;
            const desc = this.descriptions[state];
            if (desc.includes('{username}')) {
                description = desc.replace('{username}', `"${this.username}"`);
            }

            return digits(description, this.$i18n.locale);
        },

        /**
         * Determine the Channel
         *
         * @returns {string}
         */
        getChannel() {
            let channel;
            if (this.channels && this.channels.length === 1) {
                [channel] = this.channels;
            } else if (this.username && this.username.toLowerCase().match(
                /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
            )) {
                channel = 'mail';
            } else if (this.username && this.username.toLowerCase().match(/(09)[0-9][0-9]{8}/)) {
                channel = 'sms';
            } else {
                channel = 'invalid';
            }
            return channel;
        },

        /**
         * Verify Code
         * 
         * @returns {string}
         */
        verifyCode() {
            let token = '';
            Object.values(this.token).forEach((t) => {
                if (t) {
                    token += t;
                }
            });
            return token;
        },
    },

    methods: {
        digits,

        /**
         * Fetch register form config
         *
         * @returns {Promise}
         */
        fetchUsernameConfig() {
            this.$wait.start('fetch-request-config');
            return this.$services.Person.loginConfigs().then((response) => {
                this.usernameFields = response.data.results.username.accept;
                this.recaptcha = response.data.results.recaptcha;
            }).catch((error) => {
                this.handleError(error);
            }).finally(() => {
                this.$wait.end('fetch-request-config');
            });
        },

        /**
         * Form fields label and placeholder
         * 
         * @param {object} config
         * @returns {object}
         */
        usernameFieldOptions(config = this.usernameFields) {
            const text = {};
            if (config) {
                const acceptValues = config;
                if (acceptValues && acceptValues.length) {
                    if (acceptValues.length === 1) {
                        text.label = this.$tc(`fields.${acceptValues[0]}`);
                        text.placeholder = this.$t('fields.placeholder', [this.$tc(`fields.${acceptValues[0]}`)]);
                        if (acceptValues[0] === 'mobile') {
                            text.format = '(DDDD) DDD DDDD';
                        } else if (acceptValues[0] === 'code_melli') {
                            text.format = 'DDD-DDDDDD-D';
                        }
                    } else {
                        const acceptValuesTrans = acceptValues.map((item) => this.$tc(`fields.${item}`));
                        text.label = this.$tc('fields.username');
                        text.placeholder = acceptValuesTrans.join(this.$tc('fields.placeholderJoinCharacter'));
                    }
                }
            }
            return text;
        },

        /**
         * Set recaptcha token
         * (set in recaptcha field)
         *
         * @param {string | number} token - Google recaptcha token
         */
        getToken(token) {
            this.recaptchaToken = token;
        },

        /**
         * Verify recaptcha
         */
        verifyForgetRecaptcha() {
            if (this.recaptcha.active && this.recaptcha.size === 'invisible') {
                this.$refs.recaptcha.verify();
            }
        },

        /**
         * Reset recaptcha
         */
        resetRecaptcha() {
            if (this.recaptcha.active && this.recaptcha.size === 'invisible') {
                this.$refs.recaptcha.reset();
            }
        },
        
        /**
         * Start recovery request
         * (set in password recovery request form | First step)
         *
         * @returns {Promise}
         */
        async recoverRequest() {
            if (!this.recaptchaToken) {
                await this.verifyForgetRecaptcha();
            }
            await this.fetchPasswordRecoveryConfig();
            await this.passwordRecoveryRequest();
        },

        /**
         * Get Password Recovery Config
         * 
         * @returns {Promise}
         */
        fetchPasswordRecoveryConfig() {
            // The $wait variable is same as `passwordRecoveryRequest` method
            // Because it should happened at the same time
            this.$wait.start('submitting-forget-request');
            const params = {
                user_name: this.username,
            };
            return this.$services.Person.passwordRecoveryConfig(params).then((response) => {
                this.tokenCharCount = Number(response.data.results.token_count);
                this.channels = response.data.results.channels;
            }).catch((error) => {
                this.handleError(error);
            }).finally(() => {
                this.$wait.end('submitting-forget-request');
            });
        },

        /**
         * Send password recovery request
         * 
         * @returns {Promise}
         */
        passwordRecoveryRequest() {
            this.$wait.start('submitting-forget-request');
            const params = {
                channel               : this.getChannel,
                user_name             : this.username,
                'g-recaptcha-response': this.recaptchaToken,
            };
            return this.$services.Person.passwordRecoveryRequest(params).then((response) => {
                this.$toast.success(response.data.results.message);
                this.runCountdown(Number(response.data.metadata.expired_time));
                this.state = 'verify-token';
            }).catch((error) => {
                this.handleError(error);
            }).finally(() => {
                this.resetRecaptcha();
                this.verifyForgetRecaptcha();
                this.$wait.end('submitting-forget-request');
            });
        },

        /**
         * Countdown of verifyToken
         *
         * @param {string|number} expireTime
         */
        runCountdown(expireTime) {
            this.showResend = false;
            this.counter = `0${expireTime}:00`;
            const duration = (expireTime * 60) - 1;
            let timer = duration;
            let minutes;
            let seconds;

            const myCountdown = setInterval(() => {
                minutes = parseInt(timer / 60, 10);
                seconds = parseInt(timer % 60, 10);

                minutes = minutes < 10 ? `0${minutes}` : minutes;
                seconds = seconds < 10 ? `0${seconds}` : seconds;

                this.counter = `${minutes}:${seconds}`;

                // eslint-disable-next-line no-plusplus
                if (--timer < 0) {
                    timer = duration;
                }

                if (this.counter === '00:00') {
                    this.showResend = true;
                    clearInterval(myCountdown);
                }
            }, 1000);
        },

        /**
         * Verify token
         * (set in verify token form | Second step)
         *
         * @returns {Promise}
         */
        verifyToken() {
            this.$wait.start('submitting-verify-token');
            const params = {
                token                 : this.verifyCode,
                user_name             : this.username,
                'g-recaptcha-response': this.recaptchaToken,
            };

            return this.$services.Person.passwordRecoveryVerifyToken(params).then(() => {
                this.afterSuccessVerifyToken();
                this.state = 'reset-password';
            }).catch((error) => {
                this.handleError(error);
            }).finally(() => {
                this.resetRecaptcha();
                this.verifyForgetRecaptcha();
                this.$wait.end('submitting-verify-token');
            });
        },

        /**
         * Custom verify token action
         * Should be overwritten in forget page
         */
        afterSuccessVerifyToken() {},

        /**
         * Reset password
         * (set in reset password form | Final step)
         *
         * @returns {Promise}
         */
        resetPassword() {
            this.$wait.start('submitting-reset-password');

            const params = {
                user_name             : this.username,
                password              : this.password,
                password2             : this.password2,
                token                 : this.verifyCode,
                'g-recaptcha-response': this.recaptchaToken,
            };

            return this.$services.Person.passwordRecovery(params).then((response) => {
                this.token = response.data.results.access_token;
                if (this.loginAfterForget) {
                    this.$store.dispatch('auth/login', this.token);
                    this.$http.defaults.headers.common.Authorization = `Bearer ${this.token}`;
                }
                if (this.afterSuccessResetPasswordAction) {
                    this.afterSuccessResetPasswordAction();
                }
                this.$toast.success(this.$t('auth.forget.messages.success_reset'));
                this.setRedirect();
            }).catch((error) => {
                this.handleError(error);
                this.resetRecaptcha();
                this.verifyForgetRecaptcha();
            }).finally(() => {
                this.$wait.end('submitting-reset-password');
            });
        },

        /**
         * Set Redirect
         */
        setRedirect() {
            if (this.$store.getters['auth/redirect']?.name) {
                this.$router.push(this.$store.getters['auth/redirect']);
                this.$store.dispatch('auth/resetRedirect');
                localStorage.removeItem('redirect');
            } else {
                this.$router.push({ name: 'home' });
            }
        },

        /**
         * Show and hide the password
         */
        showPasswordChars() {
            this.showPassword = !this.showPassword;
        },

        /**
         * Show and hide the password2
         */
        showPassword2Chars() {
            this.showPassword2 = !this.showPassword2;
        },

        /**
         * Change input when filled
         *
         * @param {number} index
         * @param {*} val
         */
        changeInput(index, val) {
            if (val) {
                if (index < this.tokenCharCount) {
                    document.getElementsByTagName('input')[index].focus();
                }
            } else if (index > 0 && document.getElementsByTagName('input')[index - 2]) {
                document.getElementsByTagName('input')[index - 2].focus();
            }
        },
    },
};

